import { FC } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { SettingsFormData } from '@/features/settings/SettingsPage';
import { Switch } from '@/components/ui/switch';
import {
    RENT_ANALYTICS_EXPENSE_LINE_KEYS,
    rentAnalyticsExpenseLineTranslationKey,
} from '@/features/finances/constants/rentAnalyticsExpenseLines';

type Props = {
    control: Control<SettingsFormData>;
    isMobile?: boolean;
    canConfigure: boolean;
};

const SettingsRentAnalyticsExpenses: FC<Props> = ({ control, isMobile, canConfigure }) => {
    const { t } = useTranslation();

    if (!canConfigure) return null;

    return (
        <div
            className={cn(
                'w-full self-start p-6 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10',
                !isMobile && 'md:w-1/2 md:max-w-xl',
                isMobile && 'p-0 border-0 bg-popover-30',
            )}>
            <div className="flex flex-col gap-1">
                <h3 className={cn(isMobile && 'text-base')}>
                    {t('main.settings.components.rentAnalytics.title')}
                </h3>
                <p className="text-sm text-muted-foreground">
                    {t('main.settings.components.rentAnalytics.text')}
                </p>
            </div>
            <Controller
                control={control}
                name="rentAnalyticsHiddenExpenseKeys"
                render={({ field }) => {
                    const hidden = field.value ?? [];
                    return (
                        <div className="flex flex-col gap-3">
                            {RENT_ANALYTICS_EXPENSE_LINE_KEYS.map((key) => {
                                const visible = !hidden.includes(key);
                                return (
                                    <div
                                        key={key}
                                        className="flex items-center justify-between gap-4 py-1">
                                        <span className="text-sm">
                                            {t(rentAnalyticsExpenseLineTranslationKey[key])}
                                        </span>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="text-xs text-muted-foreground">
                                                {t(
                                                    visible
                                                        ? 'main.settings.components.rentAnalytics.visible'
                                                        : 'main.settings.components.rentAnalytics.hidden',
                                                )}
                                            </span>
                                            <Switch
                                                checked={visible}
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        field.onChange(hidden.filter((k) => k !== key));
                                                    } else if (!hidden.includes(key)) {
                                                        field.onChange([...hidden, key]);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                }}
            />
        </div>
    );
};

export default SettingsRentAnalyticsExpenses;
